import { AZURE } from './api';

/**
 * Serviço para APIs de Natação
 * Base URL: /api/swimming
 */

// ========== COMPETIÇÕES ==========

/**
 * Busca todas as competições de natação
 * @param {Object} filters - Filtros opcionais (year, country)
 * @returns {Promise<Array>} Lista de competições
 */
export const getCompetitions = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.year) params.append('year', filters.year);
    if (filters.country) params.append('country', filters.country);
    
    const response = await AZURE.get(`/swimming/competitions?${params.toString()}`);
    // Backend retorna { success, items, count } - usar 'items' ao invés de 'competitions'
    const data = response.data?.items || response.data?.competitions || response.data || [];
    console.log('[swimmingService] Competições recebidas:', data.length, 'items');
    // Garantir que sempre retornamos array (fix para backend retornando objeto)
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Erro ao buscar competições:', error);
    return []; // Retorna array vazio em erro ao invés de throw
  }
};

/**
 * Busca cards de competições por região (anos + categorias com resultados)
 * @returns {Promise<Array>} Lista de cards
 */
export const getCompetitionsHub = async () => {
  try {
    const response = await AZURE.get('/swimming/competitions/hub');
    return response.data?.data || [];
  } catch (error) {
    console.error('[swimmingService] Erro ao buscar hub de competições:', error);
    throw error;
  }
};

/**
 * Busca detalhes de uma competição específica
 * @param {number} competitionId - ID da competição
 * @returns {Promise<Object>} Detalhes da competição com estatísticas
 */
export const getCompetitionById = async (competitionId) => {
  try {
    const response = await AZURE.get(`/swimming/competitions/${competitionId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar competição:', error);
    throw error;
  }
};

/**
 * Busca as fases disponíveis em uma competição específica
 * @param {number} competitionId - ID da competição
 * @returns {Promise<Array>} Lista de fases disponíveis (ex: ['Final', 'Final B', 'Semifinal', 'Eliminatórias'])
 */
export const getCompetitionPhases = async (competitionId) => {
  try {
    const response = await AZURE.get(`/swimming/competitions/${competitionId}/phases`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar fases da competição:', error);
    throw error;
  }
};

/**
 * Busca eventos (provas) de uma competição com suas fases disponíveis
 * @param {number} competitionId - ID da competição
 * @param {string} phase - Fase específica para filtrar (opcional)
 * @returns {Promise<Array>} Lista de eventos com suas fases disponíveis
 */
export const getCompetitionEventsWithPhases = async (competitionId, phase = null) => {
  try {
    const params = new URLSearchParams();
    if (phase) params.append('phase', phase);
    
    const response = await AZURE.get(`/swimming/competitions/${competitionId}/events-phases?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar eventos com fases:', error);
    throw error;
  }
};

// ========== NADADORES ==========

/**
 * Busca lista de nadadores com filtros
 * @param {Object} filters - Filtros (nationality, gender, club_id)
 * @returns {Promise<Array>} Lista de nadadores
 */
export const getSwimmers = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.nationality) params.append('nationality', filters.nationality);
    if (filters.gender) params.append('gender', filters.gender);
    if (filters.club_id) params.append('club_id', filters.club_id);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    
    const response = await AZURE.get(`/swimming/swimmers?${params.toString()}`);
    const data = response.data?.items || response.data?.swimmers || response.data?.data || response.data || [];
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Erro ao buscar nadadores:', error);
    return [];
  }
};

/**
 * Busca perfil completo de um nadador
 * @param {number} swimmerId - ID do nadador
 * @returns {Promise<Object>} Perfil com resultados, medalhas e histórico
 */
export const getSwimmerById = async (swimmerId) => {
  try {
    const response = await AZURE.get(`/swimming/swimmers/${swimmerId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar nadador:', error);
    throw error;
  }
};

// Cache de países (5 minutos)
let countriesCache = null;
let countriesCacheTimestamp = null;
const COUNTRIES_CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Busca lista de países com nadadores (COM CACHE)
 * @param {boolean} forceRefresh - Forçar atualização ignorando cache
 * @returns {Promise<Array>} Lista de países com contagem de atletas e clubes
 */
export const getCountries = async (forceRefresh = false) => {
  try {
    // Verificar cache
    if (!forceRefresh && countriesCache && countriesCacheTimestamp) {
      const cacheAge = Date.now() - countriesCacheTimestamp;
      if (cacheAge < COUNTRIES_CACHE_DURATION) {
        console.log('✅ [swimmingService] Usando cache de países (idade:', Math.round(cacheAge / 1000), 's)');
        return countriesCache;
      }
    }
    
    console.log('🔄 [swimmingService] Buscando países do backend...');
    const response = await AZURE.get('/swimming/countries');
    
    // Atualizar cache
    countriesCache = response.data;
    countriesCacheTimestamp = Date.now();
    
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar países:', error);
    // Se tiver cache, usar mesmo expirado em caso de erro
    if (countriesCache) {
      console.warn('⚠️ [swimmingService] Usando cache expirado devido a erro');
      return countriesCache;
    }
    throw error;
  }
};

// ========== CLUBES ==========

/**
 * Busca lista de clubes com filtros
 * @param {Object} filters - Filtros (state, country)
 * @returns {Promise<Array>} Lista de clubes
 */
export const getClubs = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.state) params.append('state', filters.state);
    if (filters.country) params.append('country', filters.country);
    
    console.log('🔍 [swimmingService] getClubs chamado com filtros:', filters);
    console.log('🔗 [swimmingService] URL params construída:', params.toString());
    console.log('🔗 [swimmingService] URL completa:', `/swimming/clubs?${params.toString()}`);
    
    const response = await AZURE.get(`/swimming/clubs?${params.toString()}`);
    console.log('📦 [swimmingService] Resposta bruta do backend:', response);
    console.log('📦 [swimmingService] response.data:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar clubes:', error);
    throw error;
  }
};

/**
 * Busca detalhes de um clube específico
 * @param {number} clubId - ID do clube
 * @returns {Promise<Object>} Detalhes do clube com roster e estatísticas
 */
export const getClubById = async (clubId) => {
  try {
    const response = await AZURE.get(`/swimming/clubs/${clubId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar clube:', error);
    throw error;
  }
};

// ========== EVENTOS/PROVAS ==========

/**
 * Busca lista de eventos (provas) com filtros
 * @param {Object} filters - Filtros (stroke, distance, gender, is_relay)
 * @returns {Promise<Array>} Lista de eventos
 */
export const getEvents = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    // IMPORTANTE: competition_id filtra eventos que existem na competição
    if (filters.competition_id) params.append('competition_id', filters.competition_id);
    if (filters.stroke) params.append('stroke', filters.stroke);
    if (filters.distance) params.append('distance', filters.distance);
    if (filters.gender) params.append('gender', filters.gender);
    if (filters.is_relay !== undefined) params.append('is_relay', filters.is_relay);
    else if (filters.relay !== undefined) params.append('is_relay', filters.relay ? 1 : 0);
    
    const response = await AZURE.get(`/swimming/events?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar eventos:', error);
    throw error;
  }
};

// ========== RESULTADOS ==========

/**
 * Busca resultados com filtros usando VIEW OTIMIZADA do Azure
 * @param {Object} filters - Filtros (competition_id, event_id, swimmer_id, round)
 * @returns {Promise<Array>} Lista de resultados PRÉ-CALCULADOS (posições, medalhas, logos)
 */
export const getResults = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.competition_id) params.append('competition_id', filters.competition_id);
    if (filters.event_id) params.append('event_id', filters.event_id);
    if (filters.swimmer_id) params.append('swimmer_id', filters.swimmer_id);
    if (filters.limit) params.append('limit', filters.limit);
    // Backend usa 'fase' - aceita tanto 'round' quanto 'fase'
    if (filters.fase) params.append('fase', filters.fase);
    else if (filters.round) params.append('fase', filters.round);
    
    console.log('📊 Buscando resultados da VIEW OTIMIZADA com filtros:', filters);
    console.log('🔗 URL da API (usando vw_swimming_results_fast):', `/swimming/results/fast?${params.toString()}`);
    
    // Usar endpoint /fast que consulta a VIEW otimizada
    const response = await AZURE.get(`/swimming/results/fast?${params.toString()}`);
    // Backend retorna { success, count, data, results }
    const results = response.data?.data || response.data?.results || response.data || [];
    console.log('✅ Resposta da VIEW:', results.length, 'resultados');
    return results;
  } catch (error) {
    console.error('❌ Erro ao buscar resultados da VIEW:', error);
    throw error;
  }
};

/**
 * Busca pódio (top 3) de um evento específico
 * @param {number} competitionId - ID da competição
 * @param {number} eventId - ID do evento
 * @returns {Promise<Array>} Top 3 resultados agrupados por evento
 */
export const getPodiumResults = async (competitionId, eventId) => {
  try {
    const params = new URLSearchParams();
    if (competitionId) params.append('competition_id', competitionId);
    if (eventId) params.append('event_id', eventId);
    
    const response = await AZURE.get(`/swimming/results/podium?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar pódio:', error);
    throw error;
  }
};

// ========== RANKINGS ==========

/**
 * Busca ranking de melhores tempos por evento
 * @param {number} eventId - ID do evento
 * @param {number} year - Ano (opcional)
 * @param {number} limit - Limite de resultados (padrão 10)
 * @param {string} nationality - Código do país (opcional, ex: BRA, USA)
 * @returns {Promise<Object>} Ranking ordenado por melhor tempo
 */
export const getRankings = async (eventId, year = null, limit = 10, nationality = null) => {
  try {
    const params = new URLSearchParams();
    params.append('event_id', eventId);
    if (year) params.append('year', year);
    params.append('limit', limit);
    if (nationality) params.append('nationality', nationality);
    
    const response = await AZURE.get(`/swimming/rankings?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar rankings:', error);
    throw error;
  }
};

// ========== ESTATÍSTICAS GERAIS ==========

/**
 * Busca estatísticas gerais da plataforma de natação
 * @returns {Promise<Object>} Estatísticas globais (total de competições, nadadores, clubes, eventos, resultados)
 */
export const getOverviewStatistics = async () => {
  try {
    const response = await AZURE.get('/swimming/statistics/overview');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar estatísticas:', error);
    throw error;
  }
};

// ========== ANALYTICS (STATS COMPETIÇÃO) ==========

/**
 * Busca tendências históricas de uma competição (2021+)
 * @param {Object} params - { competition_name, event_id, gender, from_year, to_year, phase }
 * @returns {Promise<Object>} Série temporal e metadata
 */
export const getCompetitionTrends = async (params = {}) => {
  try {
    const response = await AZURE.get('/swimming/analytics/competition-trends', { params });
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar trends da competição:', error);
    throw error;
  }
};

/**
 * Busca dashboard demográfico de clubes para uma competição
 * @param {Object} params - { competition_id, year, gender, phase }
 * @returns {Promise<Object>} Dados agregados por estado e tipo de localidade
 */
export const getClubDemographics = async (params = {}) => {
  try {
    const response = await AZURE.get('/swimming/analytics/club-demographics', { params });
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar demografia de clubes:', error);
    throw error;
  }
};

// ========== HEALTH CHECK ==========

// ========== ANALYTICS (VIEWS OTIMIZADAS) ==========

/**
 * Busca campeões (1º lugares) de todas as competições (VIEW vw_swimming_champions)
 * @param {Object} filters - Filtros (event_id, competition_id, gender)
 * @returns {Promise<Array>} Lista de campeões com dados do atleta, clube e tempo
 */
export const getChampions = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.event_id) params.append('event_id', filters.event_id);
    if (filters.competition_id) params.append('competition_id', filters.competition_id);
    if (filters.gender) params.append('gender', filters.gender);
    
    const response = await AZURE.get(`/swimming/analytics/champions?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('❌ Erro ao buscar campeões:', error);
    throw error;
  }
};

/**
 * Busca tempos médios por competição/evento/fase (VIEW otimizada)
 * @param {Object} filters - Filtros (event_id, competition_id, gender)
 * @returns {Promise<Array>} Lista de médias por fase
 */
export const getAvgTimesByPhase = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.event_id) params.append('event_id', filters.event_id);
    if (filters.competition_id) params.append('competition_id', filters.competition_id);
    if (filters.gender) params.append('gender', filters.gender);
    if (filters.phase) params.append('phase', filters.phase);
    
    const response = await AZURE.get(`/swimming/analytics/avg-times?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('❌ Erro ao buscar tempos médios:', error);
    throw error;
  }
};

/**
 * Busca diferença entre 1º e 8º nas finais (VIEW otimizada)
 * @param {Object} filters - Filtros (event_id, competition_id, gender, phase, min_athletes)
 * @returns {Promise<Array>} Lista de diferenças por final
 */
export const getFinalTimeDifferences = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.event_id) params.append('event_id', filters.event_id);
    if (filters.competition_id) params.append('competition_id', filters.competition_id);
    if (filters.gender) params.append('gender', filters.gender);
    if (filters.phase) params.append('phase', filters.phase);
    if (filters.min_athletes !== undefined) params.append('min_athletes', filters.min_athletes);
    
    const response = await AZURE.get(`/swimming/analytics/final-differences?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('❌ Erro ao buscar diferenças das finais:', error);
    throw error;
  }
};

// ========== HEALTH CHECK ==========

/**
 * Verifica saúde da API de natação
 * @returns {Promise<Object>} Status da API
 */
export const healthCheck = async () => {
  try {
    const response = await AZURE.get('/swimming/health');
    return response.data;
  } catch (error) {
    console.error('Erro no health check:', error);
    throw error;
  }
};

// Exporta todas as funções como objeto default também
export default {
  getCompetitions,
  getCompetitionById,
  getCompetitionPhases,
  getCompetitionEventsWithPhases,
  getSwimmers,
  getSwimmerById,
  getCountries,
  getClubs,
  getClubById,
  getEvents,
  getResults,
  getPodiumResults,
  getRankings,
  getOverviewStatistics,
  getChampions,
  getAvgTimesByPhase,
  getFinalTimeDifferences,
  healthCheck
};
